import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { AdminUser, AdminLoginRequest } from '../services/admin/types';
import { adminApi } from '../services/admin/adminApi';

interface AdminStore {
  // Auth state
  admin: AdminUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // UI state
  sidebarCollapsed: boolean;
  selectedUsers: string[];
  
  // Auth actions
  login: (credentials: AdminLoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  
  // UI actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSelectedUsers: (users: string[]) => void;
  clearError: () => void;
}

export const useAdminStore = create<AdminStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        admin: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        sidebarCollapsed: false,
        selectedUsers: [],
        
        // Auth actions
        login: async (credentials: AdminLoginRequest) => {
          set({ isLoading: true, error: null });
          try {
            const response = await adminApi.login(credentials);
            set({
              admin: response.admin,
              token: response.access_token,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } catch (error: any) {
            set({
              isLoading: false,
              error: error.response?.data?.detail || 'Login failed',
              isAuthenticated: false,
            });
            throw error;
          }
        },
        
        logout: async () => {
          set({ isLoading: true });
          try {
            await adminApi.logout();
          } finally {
            set({
              admin: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
              selectedUsers: [],
            });
          }
        },
        
        checkAuth: async () => {
          const token = adminApi.getToken();
          if (!token) {
            set({ isAuthenticated: false, admin: null });
            return;
          }
          
          set({ isLoading: true });
          try {
            const admin = await adminApi.getCurrentAdmin();
            set({
              admin,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });
          } catch (error) {
            adminApi.clearToken();
            set({
              admin: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        },
        
        // UI actions
        toggleSidebar: () => {
          set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
        },
        
        setSidebarCollapsed: (collapsed: boolean) => {
          set({ sidebarCollapsed: collapsed });
        },
        
        setSelectedUsers: (users: string[]) => {
          set({ selectedUsers: users });
        },
        
        clearError: () => {
          set({ error: null });
        },
      }),
      {
        name: 'admin-storage',
        partialize: (state) => ({
          sidebarCollapsed: state.sidebarCollapsed,
        }),
      }
    )
  )
);